import { parquetRead, parquetMetadata } from 'hyparquet';
import { compressors } from 'hyparquet-compressors';
import { blobCacheKey } from '../utils/cacheKey';

// ── Cache name for client-side parquet files ──────────────────────────────
const CACHE_NAME = 'orderbook-parquet-cache';

// ── In-memory cache for OHLC rows, valid for the current calendar day ─────
// Re-fetched once the local date changes (new candles may have been appended).
const ohlcRowsCache = new Map<string, { cachedDate: string; rows: any[] }>();

function localDateKey(d = new Date()): string {
  // Cache for 15 minutes instead of the entire calendar day
  // This ensures users get the latest OHLC/EOD data shortly after it's updated on the server
  const quarterHour = Math.floor(d.getMinutes() / 15);
  return `${d.getFullYear()}-${d.getMonth() + 1}-${d.getDate()}-${d.getHours()}-${quarterHour}`;
}

/**
 * Wrap a plain ArrayBuffer into hyparquet's AsyncBuffer interface.
 */
function asyncBufferFromArrayBuffer(arrayBuffer: ArrayBuffer) {
  return {
    byteLength: arrayBuffer.byteLength,
    slice: (start: number, end?: number): Promise<ArrayBuffer> =>
      Promise.resolve(arrayBuffer.slice(start, end)),
  };
}

/**
 * Fetch a parquet file from Azure Blob (via SAS URL) or return from cache.
 * Returns raw ArrayBuffer of the parquet file.
 */
async function fetchParquet(
  ticker: string,
  dateStr: string,
  containerUrl: string,
  sasToken: string,
  onProgress?: (percent: number) => void
): Promise<ArrayBuffer> {
  const cacheKey = `order_detail/${dateStr}/${ticker}.parquet`;

  // In dev: route via Vite proxy to bypass CORS on localhost.
  // In production: fetch directly from Azure Blob (requires CORS configured on storage account).
  const isDev = (import.meta as any).env?.DEV || false;
  const blobUrl = isDev
    ? `/azure-blob-proxy/trading-data-v2/${cacheKey}?${sasToken}`
    : `${containerUrl}/${cacheKey}?${sasToken}`;

  console.log(`[Parquet] fetchParquet ticker=${ticker} date=${dateStr} isDev=${isDev}`);
  console.log(`[Parquet] blobUrl: ${blobUrl.substring(0, 150)}...`);

  // Try Cache API first
  try {
    const cache = await caches.open(CACHE_NAME);
    const cached = await cache.match(blobCacheKey(cacheKey));

    if (cached) {
      console.log(`[Parquet] ✅ Cache HIT: ${cacheKey}`);
      const buf = await cached.arrayBuffer();
      console.log(`[Parquet] Cached size: ${buf.byteLength} bytes`);
      onProgress?.(100);
      return buf;
    }

    console.log(`[Parquet] ❌ Cache MISS — fetching from Azure Blob...`);
    const response = await fetch(blobUrl);
    console.log(`[Parquet] HTTP ${response.status} ${response.statusText}`);

    if (!response.ok) {
      if (response.status === 404) {
        throw new Error(`No data found for ${ticker} on ${dateStr}. Please choose another date.`);
      }
      const errText = await response.text().catch(() => '');
      throw new Error(`HTTP ${response.status}: ${errText.substring(0, 200)}`);
    }

    const contentLength = response.headers.get('content-length');
    const total = contentLength ? parseInt(contentLength, 10) : 0;
    onProgress?.(0);

    const reader = response.body?.getReader();
    if (!reader) {
      const arrayBuffer = await response.arrayBuffer();
      onProgress?.(100);
      return arrayBuffer;
    }

    let loaded = 0;
    const chunks: Uint8Array[] = [];
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      chunks.push(value);
      loaded += value.length;
      if (total > 0) {
        onProgress?.(Math.round((loaded / total) * 100));
      }
    }

    const arrayBuffer = new Uint8Array(loaded);
    let position = 0;
    for (const chunk of chunks) {
      arrayBuffer.set(chunk, position);
      position += chunk.length;
    }

    console.log(`[Parquet] ✅ Downloaded ${arrayBuffer.byteLength} bytes`);

    // Store in cache for next time
    await cache.put(blobCacheKey(cacheKey), new Response(arrayBuffer.slice(0).buffer, {
      headers: { 'Content-Type': 'application/octet-stream' }
    }));
    console.log(`[Parquet] 💾 Saved to cache`);
    return arrayBuffer.buffer;

  } catch (err: any) {
    if (err.message?.startsWith('HTTP ')) throw err;
    // Cache API unavailable — fallback to direct fetch
    console.warn(`[Parquet] Cache API unavailable, direct fetch fallback:`, err);
    const response = await fetch(blobUrl);
    if (!response.ok) throw new Error(`HTTP ${response.status}: ${ticker} on ${dateStr} not found`);
    
    const contentLength = response.headers.get('content-length');
    const total = contentLength ? parseInt(contentLength, 10) : 0;
    onProgress?.(0);

    const reader = response.body?.getReader();
    if (!reader) {
      const arrayBuffer = await response.arrayBuffer();
      onProgress?.(100);
      return arrayBuffer;
    }

    let loaded = 0;
    const chunks: Uint8Array[] = [];
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      chunks.push(value);
      loaded += value.length;
      if (total > 0) {
        onProgress?.(Math.round((loaded / total) * 100));
      }
    }

    const arrayBuffer = new Uint8Array(loaded);
    let position = 0;
    for (const chunk of chunks) {
      arrayBuffer.set(chunk, position);
      position += chunk.length;
    }
    console.log(`[Parquet] ✅ Fallback downloaded ${arrayBuffer.byteLength} bytes`);
    return arrayBuffer.buffer;
  }
}

/**
 * Parse order details from parquet ArrayBuffer.
 */
export async function parseOrderDetails(arrayBuffer: ArrayBuffer): Promise<any[]> {
  console.log(`[Parquet] Parsing ${arrayBuffer.byteLength} bytes with hyparquet...`);

  // parquetMetadata takes raw ArrayBuffer
  const metadata = parquetMetadata(arrayBuffer as any);
  
  // The first element of schema is the root element, subsequent elements are columns
  const columnNames = (metadata.schema || [])
    .slice(1) // Skip root
    .map((f: any) => f.name);
  
  console.log(`[Parquet] Rows: ${metadata.num_rows}, Columns:`, columnNames);

  // parquetRead needs AsyncBuffer interface
  const asyncBuf = asyncBufferFromArrayBuffer(arrayBuffer);
  const rawRows: any[][] = [];
  await parquetRead({
    file: asyncBuf,
    compressors,
    onComplete: (data: any[][]) => {
      for (const row of data) {
        if (row) {
          rawRows.push(row);
        }
      }
    }
  });

  // Map array rows to objects and handle BigInt conversion
  const parsedRows = rawRows.map(rowArr => {
    const rowObj: Record<string, any> = {};
    columnNames.forEach((colName, index) => {
      let val = rowArr[index];
      // Convert BigInt to standard Number/String to avoid serialization/rendering issues
      if (typeof val === 'bigint') {
        // queue and order_no can be BigInt, convert queue to number and order_no to string
        if (colName === 'queue' || colName === 'price' || colName === 'rls' || colName === 'opn') {
          val = Number(val);
        } else {
          val = String(val);
        }
      }
      rowObj[colName] = val;
    });
    return rowObj;
  });

  console.log(`[Parquet] ✅ Parsed ${parsedRows.length} rows. Sample[0]:`, parsedRows[0]);
  return parsedRows;
}

/**
 * Load and parse a parquet file into plain JS row objects.
 */
export async function loadOrderDetails(
  ticker: string,
  dateStr: string,
  containerUrl: string,
  sasToken: string,
  onProgress?: (percent: number) => void
): Promise<any[]> {
  const arrayBuffer = await fetchParquet(ticker, dateStr, containerUrl, sasToken, onProgress);
  return parseOrderDetails(arrayBuffer);
}

/**
 * Parse done details from parquet ArrayBuffer.
 */
export async function parseDoneDetails(arrayBuffer: ArrayBuffer): Promise<any[]> {
  const metadata = parquetMetadata(arrayBuffer as any);
  const columnNames = (metadata.schema || []).slice(1).map((f: any) => f.name);
  const asyncBuf = asyncBufferFromArrayBuffer(arrayBuffer);
  const rawRows: any[][] = [];
  
  await parquetRead({
    file: asyncBuf,
    compressors,
    onComplete: (data: any[][]) => {
      for (const row of data) {
        if (row) {
          rawRows.push(row);
        }
      }
    }
  });

  const parsedRows = rawRows.map(rowArr => {
    const rowObj: Record<string, any> = {};
    columnNames.forEach((colName, index) => {
      let val = rowArr[index];
      if (typeof val === 'bigint') {
        if (['STK_VOLM', 'STK_PRIC', 'VALUE', 'TRX_SESS', 'TRX_ORD1', 'TRX_ORD2', 'HAKA_HAKI'].includes(colName)) {
          val = Number(val);
        } else {
          val = String(val);
        }
      }
      rowObj[colName] = val;
    });
    return rowObj;
  });

  return parsedRows;
}

/**
 * Load and parse a done detail parquet file into plain JS row objects.
 */
export async function loadDoneDetails(
  ticker: string,
  dateStr: string,
  containerUrl: string,
  sasToken: string
): Promise<any[]> {
  const formattedDate = dateStr.replace(/-/g, '');
  const cacheKey = `done_detail/${formattedDate}/STOCK/${ticker}.parquet`;

  // Use similar fetch logic but customized for done_detail path
  const isDev = (import.meta as any).env?.DEV || false;
  const blobUrl = isDev
    ? `/azure-blob-proxy/trading-data-v2/${cacheKey}?${sasToken}`
    : `${containerUrl}/${cacheKey}?${sasToken}`;

  console.log(`[Parquet] fetchDoneDetail ticker=${ticker} date=${dateStr}`);

  try {
    let arrayBuffer: ArrayBuffer;
    const cache = await caches.open(CACHE_NAME);
    const cached = await cache.match(blobCacheKey(cacheKey));
    if (cached) {
      arrayBuffer = await cached.arrayBuffer();
    } else {
      const response = await fetch(blobUrl);
      if (!response.ok) {
        throw new Error(`No data found for ${ticker} on ${dateStr}. HTTP ${response.status}`);
      }
      arrayBuffer = await response.arrayBuffer();
      await cache.put(blobCacheKey(cacheKey), new Response(arrayBuffer.slice(0), {
        headers: { 'Content-Type': 'application/octet-stream' }
      }));
    }

    return parseDoneDetails(arrayBuffer);
  } catch (err) {
    console.error(`[Parquet] loadDoneDetails error:`, err);
    throw err;
  }
}

/**
 * Parse OHLC from parquet ArrayBuffer.
 */
export async function parseOhlc(arrayBuffer: ArrayBuffer): Promise<any[]> {
  const metadata = parquetMetadata(arrayBuffer as any);
  const columnNames = (metadata.schema || []).slice(1).map((f: any) => f.name);
  const asyncBuf = asyncBufferFromArrayBuffer(arrayBuffer);
  const rawRows: any[][] = [];
  await parquetRead({
    file: asyncBuf,
    compressors,
    onComplete: (data: any[][]) => {
      for (const row of data) {
        if (row) {
          rawRows.push(row);
        }
      }
    },
  });

  const rows = rawRows.map(rowArr => {
    const obj: Record<string, any> = {};
    columnNames.forEach((col, i) => {
      let v = rowArr[i];
      if (typeof v === 'bigint') v = Number(v);
      obj[col] = v;
    });
    return obj;
  });

  return rows;
}

/**
 * Load and parse an OHLC parquet file given its full blob path.
 * e.g. path = "ohlc/stocks/Basic Materials/AADI.parquet"
 */
export async function loadOhlcByPath(
  blobPath: string,
  containerUrl: string,
  sasToken: string
): Promise<any[]> {
  const todayKey = localDateKey();
  const cached = ohlcRowsCache.get(blobPath);
  if (cached && cached.cachedDate === todayKey) {
    return cached.rows;
  }

  const isDev = (import.meta as any).env?.DEV || false;
  const blobUrl = isDev
    ? `/azure-blob-proxy/trading-data-v2/${blobPath}?${sasToken}`
    : `${containerUrl}/${blobPath}?${sasToken}`;

  // Cache key includes the calendar day so "today's" OHLC (which can still receive
  // new candles intraday) is re-fetched once per day, while still avoiding
  // re-downloads on subsequent loads/reloads within the same day.
  const cacheKey = `${blobPath}::${todayKey}`;
  let arrayBuffer: ArrayBuffer;
  const cache = await caches.open(CACHE_NAME);
  const cachedResponse = await cache.match(blobCacheKey(cacheKey));
  if (cachedResponse) {
    arrayBuffer = await cachedResponse.arrayBuffer();
  } else {
    // High priority so this completes ahead of the (larger) done-summary zip
    // even though both are fetched concurrently.
    const response = await fetch(blobUrl, { priority: 'high' } as RequestInit);
    if (!response.ok) throw new Error(`HTTP ${response.status}: OHLC not found at ${blobPath}`);
    arrayBuffer = await response.arrayBuffer();
    await cache.put(blobCacheKey(cacheKey), new Response(arrayBuffer.slice(0), {
      headers: { 'Content-Type': 'application/octet-stream' }
    }));
  }

  const rows = await parseOhlc(arrayBuffer);
  ohlcRowsCache.set(blobPath, { cachedDate: todayKey, rows });
  return rows;
}

/**
 * Load and parse a foreign flow parquet file for a given ticker directly from Azure Blob.
 */
export async function loadForeignFlowByTicker(
  ticker: string,
  containerUrl: string,
  sasToken: string
): Promise<any[]> {
  const cacheKey = `foreign_flow/${ticker.toUpperCase()}.parquet`;

  const isDev = (import.meta as any).env?.DEV || false;
  const blobUrl = isDev
    ? `/azure-blob-proxy/trading-data-v2/${cacheKey}?${sasToken}`
    : `${containerUrl}/${cacheKey}?${sasToken}`;

  let arrayBuffer: ArrayBuffer;
  const cache = await caches.open(CACHE_NAME);
  const cachedResponse = await cache.match(blobCacheKey(cacheKey));
  
  if (cachedResponse) {
    arrayBuffer = await cachedResponse.arrayBuffer();
  } else {
    const response = await fetch(blobUrl);
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: Foreign flow parquet not found for ${ticker}`);
    }
    arrayBuffer = await response.arrayBuffer();
    await cache.put(blobCacheKey(cacheKey), new Response(arrayBuffer.slice(0), {
      headers: { 'Content-Type': 'application/octet-stream' }
    }));
  }

  const metadata = parquetMetadata(arrayBuffer as any);
  const columnNames = (metadata.schema || []).slice(1).map((f: any) => f.name);
  const asyncBuf = asyncBufferFromArrayBuffer(arrayBuffer);
  const rawRows: any[][] = [];
  
  await parquetRead({
    file: asyncBuf,
    compressors,
    onComplete: (data: any[][]) => {
      for (const row of data) {
        if (row) {
          rawRows.push(row);
        }
      }
    },
  });

  return rawRows.map(rowArr => {
    const obj: Record<string, any> = {};
    columnNames.forEach((col, i) => {
      let v = rowArr[i];
      if (typeof v === 'bigint') v = Number(v);
      obj[col] = v;
    });
    return obj;
  });
}

/**
 * Load and parse a generic parquet file given its full blob path.
 */
export async function loadGenericParquet(
  blobPath: string,
  containerUrl: string,
  sasToken: string
): Promise<any[]> {
  const isDev = (import.meta as any).env?.DEV || false;
  const blobUrl = isDev
    ? `/azure-blob-proxy/trading-data-v2/${blobPath}?${sasToken}`
    : `${containerUrl}/${blobPath}?${sasToken}`;

  let arrayBuffer: ArrayBuffer;
  const cache = await caches.open(CACHE_NAME);
  const cacheKeyStr = blobCacheKey(`${blobPath}`);
  const cachedResponse = await cache.match(cacheKeyStr);
  
  if (cachedResponse) {
    arrayBuffer = await cachedResponse.arrayBuffer();
  } else {
    const response = await fetch(blobUrl);
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: Parquet not found for ${blobPath}`);
    }
    arrayBuffer = await response.arrayBuffer();
    await cache.put(cacheKeyStr, new Response(arrayBuffer.slice(0), {
      headers: { 'Content-Type': 'application/octet-stream' }
    }));
  }

  const metadata = parquetMetadata(arrayBuffer as any);
  const columnNames = (metadata.schema || []).slice(1).map((f: any) => f.name);
  const asyncBuf = asyncBufferFromArrayBuffer(arrayBuffer);
  const rawRows: any[][] = [];
  
  await parquetRead({
    file: asyncBuf,
    compressors,
    onComplete: (data: any[][]) => {
      for (const row of data) {
        if (row) {
          rawRows.push(row);
        }
      }
    },
  });

  return rawRows.map(rowArr => {
    const obj: Record<string, any> = {};
    columnNames.forEach((col, i) => {
      let v = rowArr[i];
      if (typeof v === 'bigint') v = Number(v);
      obj[col] = v;
    });
    return obj;
  });
}
